import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotationRequestApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, FileSpreadsheet, QrCode, Search, Trash2, RefreshCw, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';

const ROLES_ALLOWED = ['admin', 'super_admin', 'sales', 'production', 'finance'];
const ROLES_CAN_DELETE = ['admin', 'super_admin', 'sales'];

const QuotationRequests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const canAccess = ROLES_ALLOWED.includes(user?.role);
  const canDelete = ROLES_CAN_DELETE.includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    quotationRequestApi.list()
      .then(r => setItems(r.data.requests || []))
      .catch(() => toast.error('Failed to load quotation requests'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canAccess) {
      toast.error("You don't have access to Quotation Requests");
      navigate('/');
      return;
    }
    load();
  }, [canAccess, load, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.customer_name || '').toLowerCase().includes(q) ||
      (it.diamond_quality || '').toLowerCase().includes(q) ||
      (it.gold_karat || '').toLowerCase().includes(q) ||
      (it.payment_terms || '').toLowerCase().includes(q) ||
      (it.remarks || '').toLowerCase().includes(q) ||
      (it.items || []).some(item => (item.qr_code || '').toLowerCase().includes(q))
    );
  }, [items, search]);

  const exportAll = async () => {
    try {
      const r = await quotationRequestApi.exportAll();
      _download(r.data, `quotation_requests_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Exported');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to export'); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await quotationRequestApi.delete(confirmDelete.id);
      toast.success('Deleted');
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="quotation-requests-page">
      <Header title="Quotation Requests" />
      <div className="px-3 md:px-8 py-3 md:py-5 max-w-screen-2xl mx-auto space-y-4">
        {/* Header bar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customer / QR / terms"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="qr-list-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="qr-list-refresh">
            <RefreshCw className={cn('h-4 w-4 md:mr-1', loading && 'animate-spin')} /> <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button variant="outline" size="sm" onClick={exportAll} disabled={items.length === 0} data-testid="qr-list-export">
            <FileSpreadsheet className="h-4 w-4 md:mr-1" /> <span className="hidden sm:inline">Export all</span>
          </Button>
          <Button
            size="sm"
            className="bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white shadow-md shadow-fuchsia-500/20"
            onClick={() => navigate('/quotation-requests/new')}
            data-testid="qr-list-new"
          >
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-10 md:p-14 text-center">
              <QrCode className="h-12 w-12 mx-auto text-fuchsia-400 mb-3" />
              <p className="text-sm text-muted-foreground">
                {items.length === 0
                  ? 'No quotation requests yet. Tap New to scan your first design.'
                  : 'No requests match your search.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2.5" data-testid="qr-list-items">
            {filtered.map(req => (
              <Card
                key={req.id}
                className="overflow-hidden hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/quotation-requests/${req.id}`)}
                data-testid={`qr-list-item-${req.id}`}
              >
                <CardContent className="p-3 md:p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 md:h-11 md:w-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                      <QrCode className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm md:text-base truncate">{req.customer_name}</p>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{req.item_count} design{req.item_count === 1 ? '' : 's'}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground mt-1">
                        {req.gold_karat && <span>{req.gold_karat}</span>}
                        {req.diamond_quality && <span>· {req.diamond_quality}</span>}
                        {req.payment_terms && <span>· {req.payment_terms}</span>}
                        <span>· {(req.created_at || '').slice(0, 10)}</span>
                        {req.created_by && <span className="hidden sm:inline">· by {req.created_by}</span>}
                      </div>
                      {req.remarks && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{req.remarks}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-rose-600"
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(req); }}
                          data-testid={`qr-list-delete-${req.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete quotation request?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.customer_name} · {confirmDelete?.item_count || 0} design{confirmDelete?.item_count === 1 ? '' : 's'} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="qr-list-confirm-delete">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function _download(blob, filename) {
  const url = URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default QuotationRequests;
